import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import type { SelectionList } from '../../../shared/selection-lists/types';

type TaxonomyLists = Partial<{
  rics_k50: SelectionList<'rics_k50'>;
  rics_k10: SelectionList<'rics_k10'>;
}>;

export function buildRoleTaxonomyOptions(selectionLists: TaxonomyLists) {
  const options: { id: number; label: string }[] = [
    { id: 0, label: 'Default' },
  ];

  if (selectionLists?.rics_k50) {
    selectionLists.rics_k50.forEach((item) => {
      if (!item?.id) return;
      const id = parseInt(item.id, 10);
      if (id >= 1 && id <= 50) {
        options.push({
          id,
          label: item.longName || item.shortName || 'Unknown',
        });
      }
    });
  }

  if (selectionLists?.rics_k10) {
    selectionLists.rics_k10.forEach((item) => {
      if (!item?.id) return;
      const baseId = parseInt(item.id, 10);
      const id = baseId + 50;
      if (id >= 51 && id <= 60) {
        options.push({
          id,
          label: `${id} - ${item.longName || item.shortName || 'Unknown'}`,
        });
      }
    });
  }

  return options.sort((a, b) => a.id - b.id);
}

export function getRoleTaxonomyIdLabelFromLists(
  selectionLists: TaxonomyLists,
  id?: number
) {
  if (!id) return 'Default';
  const options = buildRoleTaxonomyOptions(selectionLists);
  const found = options.find((o) => o.id === id);
  return found?.label || 'Default';
}

export function resolveAdaptiveLabelFromSelection(
  selectionLists: TaxonomyLists,
  selection?: CustomRoleTaxonomySelection | null
) {
  if (!selection) return undefined;
  if (selection.default) return 'Default';
  const k50 = selection.ricsK50Id ?? undefined;
  const k10 = selection.ricsK10Id ?? undefined;
  // biome-ignore lint/complexity/noUselessUndefinedInitialization: migrated from develop
  let id: number | undefined = undefined;
  if (k50) id = parseInt(k50, 10);
  else if (k10) id = parseInt(k10, 10) + 50;
  if (!id) return undefined;
  return getRoleTaxonomyIdLabelFromLists(selectionLists, id);
}

export function resolveAdaptiveLabelFromId(
  selectionLists: TaxonomyLists,
  selection?: number | null
) {
  if (selection === null || typeof selection === 'undefined') return undefined;
  if (selection === 0) return 'Default';
  const k50 = selection < 50 ? selection : undefined;
  const k10 = selection > 50 && selection < 61 ? selection : undefined;
  const id = k50 || k10;
  if (!id) return undefined;
  return getRoleTaxonomyIdLabelFromLists(selectionLists, id);
}

/**
 * Returns a standardized display name for known taxonomy sources.
 * - onet -> ONET
 * - esco -> ESCO
 * - lightcast -> Lightcast
 */
export function getPrettyTaxonomyName(rawName?: string) {
  const name = (rawName || '').trim();
  if (!name) return 'Custom';
  const lower = name.toLowerCase();
  if (lower === 'onet') return 'ONET';
  if (lower === 'esco') return 'ESCO';
  if (lower === 'lightcast') return 'Lightcast';
  return name.charAt(0).toUpperCase() + name.slice(1);
}
